import { Container, Graphics } from 'pixi.js-legacy';
import type { Particle } from '../../entities/Particle';
import { hexStringToNumber } from '../../utils/color';

/**
 * Particle Graphics 오브젝트 풀
 */
class ParticlePool {
    private pool: Graphics[] = [];
    private active: Map<Particle, Graphics> = new Map();
    private container: Container;
    // iOS: 공격적인 풀 크기 제한 (30개)
    private maxPoolSize: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 30 : 100;

    constructor(container: Container) {
        this.container = container;
    }

    get(particle: Particle): Graphics {
        let g = this.pool.pop();

        if (!g) {
            g = new Graphics();
            // 기본 사각형 (크기 1, scale로 조절)
            g.beginFill(0xFFFFFF);
            g.drawRect(-0.5, -0.5, 1, 1);
            g.endFill();
        }

        g.position.set(particle.x, particle.y);
        g.scale.set(particle.size);
        g.alpha = particle.life;
        g.tint = hexStringToNumber(particle.color);
        g.visible = true;

        this.active.set(particle, g);
        this.container.addChild(g);

        return g;
    }

    release(particle: Particle): void {
        const g = this.active.get(particle);
        if (g) {
            g.visible = false;
            this.container.removeChild(g);
            this.active.delete(particle);
            // 풀 크기 제한 초과 시 destroy
            if (this.pool.length < this.maxPoolSize) {
                this.pool.push(g);
            } else {
                g.destroy();
            }
        }
    }

    update(particle: Particle): void {
        const g = this.active.get(particle);
        if (g) {
            g.position.set(particle.x, particle.y);
            g.alpha = particle.life;
        }
    }

    has(particle: Particle): boolean {
        return this.active.has(particle);
    }

    getActiveParticles(): IterableIterator<Particle> {
        return this.active.keys();
    }

    clear(): void {
        for (const [, g] of this.active) {
            g.visible = false;
            this.container.removeChild(g);
            // 풀 크기 제한 초과 시 destroy
            if (this.pool.length < this.maxPoolSize) {
                this.pool.push(g);
            } else {
                g.destroy();
            }
        }
        this.active.clear();
    }

    reset(): void {
        // clearAll()로 이미 컨테이너에서 제거되었으므로 참조만 정리
        this.active.clear();
        // 풀의 모든 Graphics destroy
        for (const g of this.pool) {
            g.destroy();
        }
        this.pool = [];
    }

    destroy(): void {
        this.clear();
        for (const g of this.pool) {
            g.destroy();
        }
        this.pool = [];
    }
}

/**
 * Particle 어댑터
 */
export class ParticleAdapter {
    private particlePool: ParticlePool;

    constructor(container: Container) {
        this.particlePool = new ParticlePool(container);
    }

    /**
     * Set 생성 없이 직접 동기화하여 메모리 할당 최소화
     */
    sync(particles: Particle[]): void {
        // 1. 죽은 파티클만 release
        for (const particle of this.particlePool.getActiveParticles()) {
            if (!particle.isAlive) {
                this.particlePool.release(particle);
            }
        }

        // 2. 살아있는 파티클 업데이트/추가
        for (const particle of particles) {
            if (this.particlePool.has(particle)) {
                this.particlePool.update(particle);
            } else {
                this.particlePool.get(particle);
            }
        }
    }

    clear(): void {
        this.particlePool.clear();
    }

    reset(): void {
        this.particlePool.reset();
    }

    destroy(): void {
        this.particlePool.destroy();
    }
}
